/**
 * AI trailer generation service.
 *
 * Sibling of aiAnalysisService.js: where that one extracts metadata, this one
 * asks the analyze server to design + render a movie-style trailer from the same
 * video (Gemini picks the beats/text/music mood, ffmpeg cuts + scores it). It
 * reuses the analyze payload builder so File uploads and library/CDN videos are
 * resolved identically, then adds the trailer-only params.
 *
 * Unlike metadata analysis there is no mock fallback — trailer rendering needs
 * the real server (ffmpeg). If REACT_APP_GEMINI_ANALYZE_URL isn't set the caller
 * gets a clear error rather than a fake trailer.
 *
 * Requires REACT_APP_GEMINI_ANALYZE_URL to point at the analyze server
 * (e.g. http://localhost:3002/api/v1).
 */

import axios from "axios";
import buildVideoAnalyzePayload, {
  VideoSourceError,
} from "./gemini/buildAnalyzeRequest";

const GEMINI_BASE_URL = process.env.REACT_APP_GEMINI_ANALYZE_URL || "";

export { VideoSourceError };

/** Stages surfaced to the UI while a trailer job runs (server-driven `stage` ids map onto these). */
export const TRAILER_STAGES = [
  { id: "uploading", label: "Uploading" },
  { id: "planning_trailer", label: "Directing" },
  { id: "rendering_trailer", label: "Rendering" },
];

const wait = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });

const requireServer = () => {
  if (!GEMINI_BASE_URL) {
    throw new Error(
      "AI trailer generation isn't configured. Set REACT_APP_GEMINI_ANALYZE_URL to the analyze server."
    );
  }
  return GEMINI_BASE_URL.replace(/\/$/, "");
};

/** Attach trailer-only params onto the analyze payload (multipart or JSON). */
const withTrailerParams = (payload, { durationTarget, title }) => {
  if (payload.type === "multipart") {
    if (durationTarget != null) payload.body.append("duration_target", String(durationTarget));
    if (title) payload.body.append("title", title);
    return payload;
  }
  return {
    ...payload,
    body: {
      ...payload.body,
      duration_target: durationTarget ?? null,
      title: title ?? payload.body.filename ?? null,
    },
  };
};

const startTrailerJob = async (payload) => {
  const url = `${requireServer()}/videos/generate-trailer`;
  const config =
    payload.type === "multipart"
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  const response = await axios.post(url, payload.body, config);
  const data = response.data?.data ?? response.data;
  return data?.job_id || data?.jobId;
};

const fetchTrailerStatus = async (jobId) => {
  const url = `${requireServer()}/videos/generate-trailer/${jobId}`;
  const response = await axios.get(url);
  return response.data?.data ?? response.data;
};

const pollTrailerJob = async ({ jobId, onStage, signal }) => {
  const pollMs = 2000;

  while (true) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    const status = await fetchTrailerStatus(jobId);
    if (status?.stage) onStage?.(status.stage, status.progress ?? 0);

    if (status?.status === "failed") {
      throw new Error(status.error || "Trailer generation failed. Please try again.");
    }
    if (status?.status === "complete") {
      onStage?.("complete", 100);
      return status;
    }

    await wait(pollMs, signal);
  }
};

/**
 * Generate a trailer for a video.
 *
 * @param {object}   params
 * @param {object}   params.video          File or library/CDN video descriptor.
 * @param {number}   [params.durationTarget=30] Desired trailer length in seconds.
 * @param {string}   [params.title]        Known content title (used on the title card).
 * @param {function} [params.onStage]      (stageId, pct) => void progress callback.
 * @param {AbortSignal} [params.signal]
 * @returns {Promise<{ url:string, duration:number, music:null, tagline:string, title:string, beatsUsed:number, warnings:string[] }>}
 */
export const generateTrailer = async ({
  video,
  durationTarget = 30,
  title,
  onStage,
  signal,
} = {}) => {
  const base = buildVideoAnalyzePayload(video);
  if (!base) throw new Error("No video selected for trailer generation.");

  const payload = withTrailerParams(base, { durationTarget, title });

  onStage?.(TRAILER_STAGES[0].id, 0);

  const jobId = await startTrailerJob(payload);
  if (!jobId) throw new Error("Trailer API did not return a job id.");

  const finished = await pollTrailerJob({ jobId, onStage, signal });
  const trailer = finished.result?.trailer;
  if (!trailer?.url) {
    throw new Error("Trailer job finished without producing a video.");
  }

  return { ...trailer, warnings: finished.result?.warnings || [] };
};

export default generateTrailer;
